import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Megaphone, Eye, Plus, TrendingUp } from "lucide-react";
import { Link } from "wouter";
import { usePermissions } from "@/hooks/usePermissions";

interface MarketingCampaign {
  id: number;
  name: string;
  type: string;
  status: string;
  budget: string | number;
  spent: string | number;
  leads: number;
  conversions: number;
  startDate: string;
  endDate: string | null;
  description: string | null;
  createdAt: string;
}

export function MarketingCampaigns() {
  const { can, isLoading: permissionsLoading } = usePermissions();
  const hasPermission = can("marketing:view");

  const { data: campaigns = [], isLoading } = useQuery<MarketingCampaign[]>({
    queryKey: ["/api/marketing-campaigns"],
    enabled: hasPermission,
  });

  // Don't render if user doesn't have permission
  if (!permissionsLoading && !hasPermission) {
    return null;
  }

  const recentCampaigns = [...(campaigns ?? [])]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 3);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active": return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "paused": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      case "completed": return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case "draft": return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
      default: return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "email": return "📧";
      case "social": return "📱";
      case "webinar": return "🎥";
      case "referral": return "🤝";
      case "direct_mail": return "📬";
      default: return "📊";
    }
  };

  const calculateROI = (budget: string | number, conversions: number) => {
    const budgetNum = typeof budget === 'string' ? parseFloat(budget) : budget;
    if (!budgetNum || budgetNum === 0) return 0;
    // Assuming average conversion value of $2,000
    const revenue = conversions * 2000;
    return ((revenue - budgetNum) / budgetNum * 100);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString();
  };

  const formatCurrency = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return num ? num.toLocaleString() : '0';
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-lg font-medium">Marketing Campaigns</CardTitle>
          <Megaphone className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-lg font-medium">Marketing Campaigns</CardTitle>
        <Megaphone className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {recentCampaigns.length === 0 ? (
          <div className="text-center py-6">
            <Megaphone className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500 mb-4">No campaigns yet</p>
            <Link href="/marketing">
              <Button variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Create Campaign
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {recentCampaigns.map((campaign) => {
              const roi = calculateROI(campaign.budget, campaign.conversions);
              return (
                <div key={campaign.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm">{getTypeIcon(campaign.type)}</span>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {campaign.name}
                      </p>
                      <Badge className={`text-xs ${getStatusColor(campaign.status)}`}>
                        {campaign.status}
                      </Badge>
                      {roi > 0 && (
                        <Badge className="text-xs bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300">
                          <TrendingUp className="h-3 w-3 mr-1" />
                          {roi.toFixed(0)}% ROI
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center text-xs text-gray-500 mt-1 space-x-4">
                      <span>{campaign.leads} leads</span>
                      <span>{campaign.conversions} conversions</span>
                      <span>${formatCurrency(campaign.spent)}/${formatCurrency(campaign.budget)}</span>
                      <span>Started {formatDate(campaign.startDate)}</span>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm">
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
            <div className="pt-2 border-t">
              <Link href="/marketing">
                <Button variant="ghost" size="sm" className="w-full">
                  View All Campaigns ({campaigns.length})
                </Button>
              </Link>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}